import {
  iconDadosUsuario,
  iconSegurancaDesbloqueado,
} from '@bsds-lib-component/icons/icons';
import { BsdsIcon, BsdsModal } from '@bsds-lib-component/react';
import React, { useId, useMemo } from 'react';

import { ButtonClose, SystemMessage } from '../../..';
import { formatDocSubestipulante, formatVigenciaSubDisplay } from '../../../../hooks';
import type { UseSubEstipulanteResumoReturn } from '../../../../hooks/vida/useSubEstipulanteInfo';
import type {
  SubEstipulanteCoverageItem,
  SubEstipulanteInfoField,
} from '../../../../types/vida/cadastralMaintenance/subEstipulanteInfoTypes';
import type { ListaSubestipulanteItem } from '../../../../types/vida/subEstipulante/subEstipulanteType';

type SubEstipulanteResumoModalProps = {
  subestipulanteResumo: UseSubEstipulanteResumoReturn;
  numeroApolice?: string;
  onViewSegurados?: (row: ListaSubestipulanteItem) => void;
  onViewProponentes?: (row: ListaSubestipulanteItem) => void;
};

type ResumoSectionHeaderProps = {
  title: string;
};

type StatusBadgeProps = {
  label: string;
  variant: 'green' | 'gray';
};

const ResumoSectionHeader: React.FC<ResumoSectionHeaderProps> = ({ title }) => (
  <div className="flex items-center gap-2 bsds-mb-12">
    <BsdsIcon icon={iconDadosUsuario} className="w-4 h-4 text-gray-700" aria-hidden="true" />
    <h6 className="text-sm font-semibold text-gray-900">{title}</h6>
  </div>
);

const StatusBadge: React.FC<StatusBadgeProps> = ({ label, variant }) => {
  const variantClass = variant === 'green'
    ? 'bg-green-500 text-white'
    : 'bg-gray-200 text-gray-700';

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shrink-0 ${variantClass}`}>
      {label}
    </span>
  );
};

const CoverageCapitalDetails: React.FC<{ cobertura: SubEstipulanteCoverageItem }> = ({ cobertura }) => {
  const details = [
    { label: 'Capital Uniforme', value: cobertura.capitalUniforme },
    { label: 'Limite de Capital', value: cobertura.limiteCapital },
    { label: 'Limite Fixo', value: cobertura.limiteFixo },
    { label: 'Percentual sobre Básica', value: cobertura.percentualBasica },
    { label: 'Prêmio', value: cobertura.premio },
  ].filter((detail) => detail.value);

  if (details.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 space-y-1">
      {details.map((detail) => (
        <p key={detail.label} className="text-xs text-gray-500">
          {detail.label}: <span className="font-medium text-gray-700">{detail.value}</span>
        </p>
      ))}
    </div>
  );
};

const CoverageItem: React.FC<{ cobertura: SubEstipulanteCoverageItem }> = ({ cobertura }) => (
  <div
    className={`flex items-center justify-between gap-4 rounded-md border px-4 py-3 ${cobertura.ativo
        ? 'border-green-200 bg-green-50/60'
        : 'border-gray-100 bg-gray-50'
      }`}
  >
    <div className="flex items-center gap-3 min-w-0">
      <BsdsIcon
        icon={iconSegurancaDesbloqueado}
        className={`w-4 h-4 shrink-0 ${cobertura.ativo ? 'text-green-600' : 'text-gray-400'}`}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">{cobertura.nome}</p>
        <p className="text-xs text-gray-500">Capital Segurado: {cobertura.capitalSegurado}</p>
        <CoverageCapitalDetails cobertura={cobertura} />
      </div>
    </div>
    <StatusBadge
      label={cobertura.ativo ? 'Ativo' : 'Inativo'}
      variant={cobertura.ativo ? 'green' : 'gray'}
    />
  </div>
);

const RegraItem: React.FC<{ field: SubEstipulanteInfoField }> = ({ field }) => (
  <div className="flex items-center justify-between gap-4 rounded-md border border-gray-100 bg-white px-4 py-3">
    <span className="text-sm text-gray-600">{field.label}</span>
    <span className="text-sm font-semibold text-gray-900 text-right">{field.value}</span>
  </div>
);

const formatNumeroApolice = (numeroApolice?: string): string => {
  const raw = numeroApolice?.trim();
  if (!raw) return '—';
  return raw.toUpperCase().startsWith('APO-') ? raw : `APO-${raw}`;
};

const SubEstipulanteResumoModal: React.FC<SubEstipulanteResumoModalProps> = ({
  subestipulanteResumo,
  numeroApolice,
}) => {
  const modalId = useId();
  const {
    selectedSubestipulante: subestipulante,
    isOpen,
    coberturas,
    regrasELimites,
    isLoading,
    error,
    close: onClose,
  } = subestipulanteResumo;

  const resumoData = useMemo(() => {
    if (!subestipulante) {
      return null;
    }

    return {
      titulo: subestipulante.nomeSubestipulanteOut?.trim() || 'Sub Estipulante',
      dataCriacao: formatVigenciaSubDisplay(subestipulante.dataEmissaoOut),
      nomeFantasia: subestipulante.nomeComplSubestipulanteOut?.trim()
        || subestipulante.nomeSubestipulanteOut?.trim()
        || '—',
      documento: formatDocSubestipulante(subestipulante.numDocumentoOut),
      numeroApolice: formatNumeroApolice(numeroApolice),
      status: subestipulante.dsCstatus?.trim() || '—',
      vigenciaInicio: formatVigenciaSubDisplay(subestipulante.dataIniVigenciaOut),
      vigenciaFim: formatVigenciaSubDisplay(subestipulante.dataFimVigenciaOut),
    };
  }, [subestipulante, numeroApolice]);

  return (
    <BsdsModal
      isOpen={isOpen}
      onBsdsModalDidDismiss={onClose}
      backdropDismiss
      color="secondary"
      canDismiss
      animated
      showBackdrop
      keepContentsMounted
      platform="web"
      aria-labelledby={modalId}
      aria-describedby={`${modalId}-description`}
    >
      <div
        className="modal-content flex flex-col h-screen max-h-screen md:h-[75vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalId}
        aria-busy={isLoading}
      >
        {resumoData && subestipulante && (
          <>
            <div className="relative flex items-start gap-3 p-5 pb-4 shrink-0 border-b border-gray-100">
              <div className="absolute top-4 right-4">
                <ButtonClose isModal onClick={onClose} />
              </div>

              <BsdsIcon
                icon={iconSegurancaDesbloqueado}
                className="w-5 h-5 text-primary-700 mt-1 shrink-0"
                aria-hidden="true"
              />
              <div className="pr-12 min-w-0">
                <h5 id={modalId} className="text-lg font-semibold text-gray-900 bsds-mb-4">
                  {resumoData.titulo}
                </h5>
                <p className="text-sm text-gray-500">
                  Criada em {resumoData.dataCriacao}
                </p>
              </div>
            </div>

            <div
              id={`${modalId}-description`}
              className="overflow-y-auto custom-scrollbar flex-1 px-5 py-4 min-h-0 space-y-6"
              role="region"
              aria-label="Resumo do subestipulante"
            >
              {error && <SystemMessage message={error} type="error" />}

              {isLoading && (
                <p className="text-sm text-gray-500">Carregando resumo do Sub-Estipulante...</p>
              )}

              <section aria-label="Informações do Sub-Estipulante">
                <ResumoSectionHeader title="Informações do Sub-Estipulante" />
                <div className="rounded-lg bg-gray-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">Nome Fantasia</span>
                    <span className="text-sm font-semibold text-gray-900">{resumoData.nomeFantasia}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">CNPJ/ CPF</span>
                    <span className="text-sm font-semibold text-gray-900">{resumoData.documento}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">Número da Apólice</span>
                    <span className="text-sm font-semibold text-gray-900">{resumoData.numeroApolice}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">Status</span>
                    <span className="text-sm font-semibold text-gray-900">{resumoData.status}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">Início da Vigência</span>
                    <span className="text-sm font-semibold text-gray-900">{resumoData.vigenciaInicio}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">Fim da Vigência</span>
                    <span className="text-sm font-semibold text-gray-900">{resumoData.vigenciaFim}</span>
                  </div>
                </div>
              </section>

              <section aria-label="Coberturas disponíveis">
                <ResumoSectionHeader title="Coberturas Disponíveis" />
                <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                  {!isLoading && coberturas.length === 0 && (
                    <p className="text-sm text-gray-500">Nenhuma cobertura encontrada.</p>
                  )}
                  {coberturas.map((cobertura) => (
                    <CoverageItem key={cobertura.id} cobertura={cobertura} />
                  ))}
                </div>
              </section>

              <section aria-label="Regras e limites">
                <ResumoSectionHeader title="Regras e Limites" />
                <div className="rounded-lg border border-gray-200 p-3 space-y-3 mb-40">
                  {!isLoading && regrasELimites.length === 0 && (
                    <p className="text-sm text-gray-500">Nenhuma regra ou limite encontrado.</p>
                  )}
                  {regrasELimites.map((field) => (
                    <RegraItem key={field.label} field={field} />
                  ))}
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </BsdsModal>
  );
};

export default SubEstipulanteResumoModal;
